<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { format } from '@formkit/tempo'
import { useNewsStore } from '@stores/news'
import markdownit from 'markdown-it'
import Hero from '@sections/global/Hero.vue'
import Loading from '@components/global/Loading.vue'
import NewsCard from '@components/news/NewsCard.vue'

const news = useNewsStore()
const route = useRoute()
const router = useRouter()
const loading = ref(false)
const article = ref({})
const requestArticle = ref(null)
const slug = computed(() => route.params.slug)
const md = markdownit()

const breadcrumbs = ref([
	{
		label: 'Back to News',
		to: { name: 'news' },
	},
])

const body = computed(() => {
	return article.value.body ? md.render(article.value.body) : null
})
const relatedArticles = computed(() => {
	return news.articles.filter((article) => article.slug !== slug.value)
})

function cancelArticle() {
	if (requestArticle.value) requestArticle.value.cancel()
}
function getArticle() {
	loading.value = true
	article.value = {}
	cancelArticle()
	let axiosSource = axios.CancelToken.source()
	requestArticle.value = { cancel: axiosSource.cancel }
	axios
		.get(`/api/news/${route.params.slug}`, {
			cancelToken: axiosSource.token,
		})
		.then((a) => {
			loading.value = false
			article.value = a.data
		})
		.catch((e) => {
			if (e?.response?.status === 404) {
				router.push('/news')
			}
		})
}
getArticle()

watch(slug, async () => {
	getArticle()
})
</script>

<template>
	<Loading v-if="loading" />
	<template v-else>
		<section class="mx-auto max-w-(--breakpoint-xl)">
			<Hero :title="article.title" :breadcrumbs="breadcrumbs" :description="format({ date: article.published_at, format: 'full', tz: 'UTC' })" />
		</section>
		<section class="mb-10" v-if="article.header_image">
			<div class="container">
				<img class="max-h-120 w-full rounded-lg object-cover" :src="article.header_image" :alt="article.title" />
			</div>
		</section>
		<section class="mx-auto mb-10">
			<div class="container">
				<div class="grid grid-cols-3 gap-5">
					<div class="col-span-3 xl:col-span-2">
						<div class="markdown" v-html="body"></div>
					</div>
					<div class="hidden min-h-full min-w-64 flex-col items-start justify-start gap-5 pr-1 xl:flex">
						<NewsCard class="w-full" compact v-for="article in relatedArticles.slice(0, 3)" :key="article.slug" :article="article" />
					</div>
				</div>
			</div>
		</section>
	</template>
</template>
